import { styled } from "@linaria/react";
import cn from "classnames";
import React from "react";
import type { Variant } from "../Button";
import Button from "../Button";
import t from "../i18n";
import { colors } from "../Theme";
import Spinner from "../Spinner";

const CANCEL_CN = "cancel";
const OK_CN = "ok";

interface P {
  /**
   * null => no OK button
   * true => form submit OK button
   * false => disabled OK button
   * function => OK button that calls it on click
   */
  onOk: (() => void) | boolean | null;
  /**
   * null => no CANCEL button
   * false => disabled CANCEL button
   * function => CANCEL button that calls it on click
   */
  onCancel: (() => void) | false | null;
  loading: boolean;
  okButtonName?: string;
  okButtonVariant?: Variant;
  cancelButtonName?: string;
  centerButtons?: boolean;
  focusCancel?: boolean;
  focusOk?: boolean;
}

const ModalFooterWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 8px;

  button.centerButtons {
    margin: 0 calc(2%);
    max-width: 200px;
  }
`;

const ModalFooter = ({
  onOk,
  onCancel,
  okButtonName = t("modal.confirm"),
  cancelButtonName = t("modal.cancel"),
  centerButtons = false,
  loading,
  focusCancel = false,
  focusOk = false,
  okButtonVariant = "primary",
}: P) => {
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const okRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (focusCancel && cancelRef.current) cancelRef.current.focus({ preventScroll: true });
    if (focusOk && okRef.current) okRef.current.focus({ preventScroll: true });
  }, [focusCancel, focusOk]);

  return (
    <>
      {(onCancel || onOk) && (
        <ModalFooterWrapper>
          {onCancel === null ? null : (
            <Button
              ref={cancelRef}
              className={cn({ [CANCEL_CN]: true, centerButtons })}
              variant="ghost"
              onClick={typeof onCancel === "function" ? onCancel : undefined}
              disabled={loading || onCancel === false}
              data-cy="cancel-button"
            >
              {cancelButtonName}
            </Button>
          )}
          {onOk === null ? null : (
            <Button
              ref={okRef}
              className={cn({ [OK_CN]: true, centerButtons })}
              variant={okButtonVariant}
              type={typeof onOk === "boolean" ? "submit" : "button"}
              onClick={typeof onOk === "function" ? onOk : undefined}
              disabled={loading || onOk === false}
              data-cy="valid-button"
              leftIcon={loading ? <Spinner color={colors.hydrogen} /> : undefined}
            >
              {okButtonName}
            </Button>
          )}
        </ModalFooterWrapper>
      )}
    </>
  );
};

export default React.memo(ModalFooter);
